var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createSlice } from '@reduxjs/toolkit';
import { useCommonDispatcher, useCreateSliceReducer } from '@components/Shared/State/hooks';
import { CommonReducersActions } from '@components/Shared/State/store';
import axios from 'axios';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
const allSlices = {};
const createSearchSlice = (key) => {
    const createNewSlice = () => createSlice({
        name: `Search_${key}`,
        initialState: {
            filters: {
                page: 1,
                limit: 20,
                q: '',
            },
            items: [],
            hasPrevious: false,
            hasNext: false,
            loading: false,
            total: 0,
            totalPage: 1,
        },
        reducers: Object.assign({ setLoading: (state, payload) => {
                state.loading = payload.payload;
            }, setItems: (state, payload) => {
                const total = payload.payload.total;
                const totalPage = total > 0 ? Math.ceil(total / state.filters.limit) : 0;
                state.loading = false;
                state.items = payload.payload.items;
                state.total = total;
                state.totalPage = totalPage;
                state.hasPrevious = state.filters.page > 1;
                state.hasNext = state.filters.page < totalPage;
            }, setQuery: (state, payload) => {
                state.filters.page = 1;
                state.filters.q = payload.payload;
            } }, CommonReducersActions()),
    });
    if (allSlices[key]) {
        return allSlices[key];
    }
    allSlices[key] = createNewSlice();
    return allSlices[key];
};
export const useSearch = (path, key = 'main', params, fetchInterval) => {
    const [fetchKey, setFetchKey] = useState(new Date().getTime());
    const [state, actions] = useCreateSliceReducer(createSearchSlice(key));
    const { setLoading, setItems } = actions;
    const commonDispatcher = useCommonDispatcher();
    useEffect(() => {
        if (fetchInterval) {
            const interval = setInterval(() => {
                setFetchKey(new Date().getTime());
            }, fetchInterval);
            return () => clearInterval(interval);
        }
    }, [fetchInterval]);
    useEffect(() => {
        const abortController = new AbortController();
        const fetchProduct = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            setLoading(true);
            try {
                let items = [];
                let total = 0;
                if (state.filters.extra !== undefined) {
                    const result = yield axios.post(path, state.filters, {
                        signal: abortController.signal,
                        params,
                    });
                    items = (_a = result.data.rows) !== null && _a !== void 0 ? _a : result.data.items;
                    total = (_c = (_b = result.data) === null || _b === void 0 ? void 0 : _b.count) !== null && _c !== void 0 ? _c : (_d = result.data) === null || _d === void 0 ? void 0 : _d.total;
                }
                else {
                    const result = yield axios.get(path, {
                        params: Object.assign(Object.assign({}, state.filters), (params !== null && params !== void 0 ? params : {})),
                        signal: abortController.signal,
                    });
                    items = (_e = result.data.rows) !== null && _e !== void 0 ? _e : result.data.items;
                    total = (_g = (_f = result.data) === null || _f === void 0 ? void 0 : _f.count) !== null && _g !== void 0 ? _g : (_h = result.data) === null || _h === void 0 ? void 0 : _h.total;
                }
                setItems({
                    items,
                    total,
                });
            }
            catch (error) {
                setLoading(false);
            }
        });
        const fetch = debounce(fetchProduct, 500);
        fetch();
        return () => {
            abortController.abort();
            fetch.cancel();
        };
    }, [params, path, setItems, setLoading, state.filters, fetchKey]);
    const newActions = useMemo(() => {
        const dispatch = (newState) => commonDispatcher(`Search_${key}`, newState);
        const previousPage = () => dispatch((currentState) => {
            currentState.filters.page--;
        });
        const nextPage = () => dispatch((currentState) => {
            currentState.filters.page++;
        });
        const setPage = (page) => dispatch((currentState) => {
            currentState.filters.page = page;
        });
        const queryClear = () => dispatch((currentState) => {
            currentState.filters.q = '';
            currentState.filters.page = 1;
        });
        const setLimit = (limit) => dispatch((currentState) => {
            currentState.filters.page = 1;
            currentState.filters.limit = limit;
        });
        const setPageAndLimit = (page, limit) => dispatch((currentState) => {
            currentState.filters.page = page;
            currentState.filters.limit = limit;
        });
        const setExtraFilters = (filters) => dispatch((currentState) => {
            currentState.filters.extra = Object.assign(Object.assign({}, currentState.filters.extra), filters);
        });
        return Object.assign(Object.assign({}, actions), { dispatch,
            previousPage,
            nextPage,
            queryClear,
            setPage,
            setLimit,
            setPageAndLimit,
            setExtraFilters });
    }, [actions, commonDispatcher, key]);
    return [state, newActions];
};
